import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { CostTier, ModelCapabilities, VerdictStatus } from "@/lib/api";
import { COST_TIERS, groupByVendor, modelName, vendorName, vendorOf } from "@/lib/models";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Plus, Server } from "lucide-react";
import { useId, useMemo, useState, type ReactNode } from "react";
import { scopeSvgIds } from "./svg-utils";
import { ENDPOINT_LOGOS, VENDOR_LOGOS, type EndpointKind } from "./vendor-logos";

// Shared pieces for model pickers and model lists: a vendor mark, compact
// capability chips, fitness verdict badges, and a searchable picker grouped by
// vendor (Devin-style: name first, one line on what it's good at, the facts
// that matter on the right).

const VENDOR_TINTS = [
  "bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300",
  "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
  "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300",
  "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  "bg-teal-100 text-teal-800 dark:bg-teal-500/15 dark:text-teal-300",
  "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300",
];

const FIXED_TINT: Record<string, number> = { anthropic: 0, openai: 3, google: 1, deepseek: 7, moonshotai: 2, minimax: 4, voyage: 6 };

function tintFor(vendor: string): string {
  let i = FIXED_TINT[vendor];
  if (i === undefined) {
    let h = 0;
    for (const ch of vendor) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    i = h % VENDOR_TINTS.length;
  }
  return VENDOR_TINTS[i];
}

const MARK_SIZES = {
  xs: { tile: "size-4 rounded", logo: "text-[11px]", letter: "text-[9px]", icon: "size-2.5" },
  sm: { tile: "size-5 rounded-md", logo: "text-[13px]", letter: "text-[10px]", icon: "size-3" },
  md: { tile: "size-6 rounded-md", logo: "text-[15px]", letter: "text-[11px]", icon: "size-3.5" },
  lg: { tile: "size-8 rounded-lg", logo: "text-[19px]", letter: "text-sm", icon: "size-4" },
} as const;

// The vendor's logo on a neutral tile; a tinted initial for vendors without
// one; a server glyph (or the server's own logo) for custom endpoints.
export function VendorMark({
  vendor,
  custom,
  endpoint,
  size = "md",
  className,
}: {
  vendor: string;
  custom?: boolean;
  endpoint?: EndpointKind | null;
  size?: keyof typeof MARK_SIZES;
  className?: string;
}) {
  const s = MARK_SIZES[size];
  const logo = custom ? (endpoint ? ENDPOINT_LOGOS[endpoint] : undefined) : VENDOR_LOGOS[vendor];
  const instance = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const scoped = useMemo(() => (logo ? scopeSvgIds(logo, instance) : ""), [logo, instance]);
  if (logo) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center bg-background text-foreground ring-1 ring-border ring-inset",
          s.tile,
          className,
        )}
        aria-hidden="true"
      >
        <span className={cn("inline-flex leading-none", s.logo)} dangerouslySetInnerHTML={{ __html: scoped }} />
      </span>
    );
  }
  if (custom) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center bg-muted text-muted-foreground ring-1 ring-border ring-inset",
          s.tile,
          className,
        )}
        aria-hidden="true"
      >
        <Server className={s.icon} />
      </span>
    );
  }
  return (
    <span
      className={cn("inline-flex shrink-0 items-center justify-center font-semibold", s.tile, s.letter, tintFor(vendor), className)}
      aria-hidden="true"
    >
      {vendorName(vendor).charAt(0)}
    </span>
  );
}

function Chip({ children, title }: { children: ReactNode; title: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex h-5 items-center rounded border px-1.5 text-[10px] font-medium tabular-nums text-muted-foreground">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );
}

export function ModelMeta({
  capabilities,
  costTier,
  className,
}: {
  capabilities?: ModelCapabilities;
  costTier?: CostTier;
  className?: string;
}) {
  // Only what a buyer weighs: price, and whether it reads images. Context
  // size and caching stay out of sight.
  if (!costTier && !capabilities?.vision) return null;
  return (
    <span className={cn("flex shrink-0 items-center gap-1", className)}>
      {capabilities?.vision && <Chip title="Understands images">Reads images</Chip>}
      {costTier && <Chip title={`${COST_TIERS[costTier].label} pricing`}>{COST_TIERS[costTier].marks}</Chip>}
    </span>
  );
}

const VERDICT_STYLE: Record<VerdictStatus, { label: string; className: string }> = {
  pass: { label: "Good fit", className: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30" },
  warn: { label: "Works, with limits", className: "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30" },
  unavailable: { label: "Not available", className: "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30" },
};

export function VerdictBadge({ status, note, className }: { status: VerdictStatus; note?: string; className?: string }) {
  const style = VERDICT_STYLE[status] ?? VERDICT_STYLE.warn;
  const badge = (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center rounded-full px-2 text-[11px] font-medium ring-1 ring-inset",
        style.className,
        className,
      )}
    >
      {style.label}
    </span>
  );
  if (!note) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent className="max-w-xs">{note}</TooltipContent>
    </Tooltip>
  );
}

// ── Picker ──────────────────────────────────────────────────────────────────

export type PickerModel = {
  name: string;
  summary?: string;
  capabilities?: ModelCapabilities;
  cost_tier?: CostTier;
  // Your own model service's id when the model is served by one, and its
  // display name.
  endpoint?: string;
  endpointLabel?: string;
  // What serves that endpoint, for its logo.
  endpointKind?: EndpointKind | null;
  // Shown instead of the summary, muted (e.g. "Key needed").
  note?: string;
  // Listed but can't be picked for this purpose (shown greyed, with `note`).
  disabled?: boolean;
  verdict?: VerdictStatus;
};

export type PickerSpecial = { value: string; label: string; detail?: string; disabled?: boolean };

// The last item of a picker: add a model from your own endpoint, or (for
// people who can't) who to ask.
export type PickerAddOwn = { onSelect: () => void } | { hint: string };

function displayOf(m: Pick<PickerModel, "name" | "endpoint">): string {
  return modelName(m.name, { custom: !!m.endpoint });
}

export function ModelPicker({
  value,
  onChange,
  models,
  specials = [],
  disabled,
  className,
  ariaLabel,
  addOwn,
  open: openProp,
  onOpenChange,
}: {
  value: string;
  onChange: (value: string) => void;
  models: PickerModel[];
  specials?: PickerSpecial[];
  disabled?: boolean;
  className?: string;
  ariaLabel: string;
  addOwn?: PickerAddOwn;
  // Controlled open state, for pages that open a picker from elsewhere.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const selectedModel = models.find((m) => m.name === value);
  const selectedSpecial = specials.find((s) => s.value === value);
  const hosted = models.filter((m) => !m.endpoint);
  const custom = models.filter((m) => m.endpoint);
  const endpoints = [...new Set(custom.map((m) => m.endpoint as string))];

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  const item = (m: PickerModel) => (
    <CommandItem
      key={m.name}
      value={`${m.name} ${displayOf(m)} ${m.endpointLabel ?? m.endpoint ?? vendorName(vendorOf(m.name))}`}
      onSelect={() => pick(m.name)}
      disabled={m.disabled}
      className="items-start gap-2 py-2"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{displayOf(m)}</span>
          {m.verdict && <VerdictBadge status={m.verdict} />}
        </div>
        {(m.note || m.summary) && (
          <p className={cn("line-clamp-1 text-xs text-muted-foreground", m.note && "italic")}>{m.note ?? m.summary}</p>
        )}
      </div>
      <ModelMeta capabilities={m.capabilities} costTier={m.cost_tier} className="mt-0.5" />
      <Check className={cn("mt-0.5 size-4 shrink-0", value === m.name ? "opacity-100" : "opacity-0")} />
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={`${ariaLabel}: ${selectedModel ? displayOf(selectedModel) : (selectedSpecial?.label ?? (value || "none"))}`}
          disabled={disabled}
          className={cn("h-10 w-full justify-between gap-2 px-2.5 font-normal transition-transform active:scale-[0.99]", className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            {selectedModel ? (
              <>
                <VendorMark
                  vendor={vendorOf(selectedModel.name)}
                  custom={!!selectedModel.endpoint}
                  endpoint={selectedModel.endpointKind}
                />
                <span className={cn("truncate font-medium", selectedModel.disabled && "text-muted-foreground line-through")}>
                  {displayOf(selectedModel)}
                </span>
                <span
                  className={cn(
                    "hidden truncate text-xs sm:inline",
                    selectedModel.disabled && selectedModel.note ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground",
                  )}
                >
                  {selectedModel.disabled && selectedModel.note
                    ? selectedModel.note
                    : (selectedModel.endpointLabel ?? selectedModel.endpoint ?? vendorName(vendorOf(selectedModel.name)))}
                </span>
              </>
            ) : selectedSpecial ? (
              <span className="truncate">
                {selectedSpecial.label}
                {selectedSpecial.detail && <span className="ml-1.5 text-xs text-muted-foreground">{selectedSpecial.detail}</span>}
              </span>
            ) : value ? (
              <span className="truncate font-mono text-xs">{value}</span>
            ) : (
              <span className="text-muted-foreground">Choose a model</span>
            )}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(28rem,calc(100vw-2rem))] p-0" align="start" collisionPadding={12}>
        <Command>
          <CommandInput placeholder="Search models" />
          <CommandList className="max-h-[min(22rem,calc(var(--radix-popover-content-available-height)-6rem))]">
            <CommandEmpty>No models match.</CommandEmpty>
            {specials.length > 0 && (
              <>
                <CommandGroup>
                  {specials.map((s) => (
                    <CommandItem
                      key={s.value}
                      value={`__${s.label} ${s.detail ?? ""}`}
                      onSelect={() => pick(s.value)}
                      disabled={s.disabled}
                    >
                      <span className="flex-1 truncate">
                        {s.label}
                        {s.detail && <span className="ml-1.5 text-xs text-muted-foreground">{s.detail}</span>}
                      </span>
                      <Check className={cn("size-4 shrink-0", value === s.value ? "opacity-100" : "opacity-0")} />
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            {endpoints.map((ep) => (
              <CommandGroup
                key={`ep-${ep}`}
                heading={
                  <span className="flex items-center gap-1.5">
                    <VendorMark vendor="" custom endpoint={custom.find((m) => m.endpoint === ep)?.endpointKind} size="xs" />
                    {custom.find((m) => m.endpoint === ep)?.endpointLabel ?? ep} · your own models
                  </span>
                }
              >
                {custom.filter((m) => m.endpoint === ep).map(item)}
              </CommandGroup>
            ))}
            {groupByVendor(hosted).map((g) => (
              <CommandGroup
                key={g.vendor}
                heading={
                  <span className="flex items-center gap-1.5">
                    <VendorMark vendor={g.vendor} size="xs" />
                    {vendorName(g.vendor)}
                  </span>
                }
              >
                {g.models.map(item)}
              </CommandGroup>
            ))}
          </CommandList>
          {addOwn && (
            <div className="border-t p-1">
              {"onSelect" in addOwn ? (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm font-medium outline-none hover:bg-accent focus-visible:bg-accent"
                  onClick={() => {
                    setOpen(false);
                    addOwn.onSelect();
                  }}
                >
                  <span className="inline-flex size-5 items-center justify-center rounded-md border border-dashed text-muted-foreground">
                    <Plus className="size-3" />
                  </span>
                  Add your own model
                </button>
              ) : (
                <p className="px-2 py-2 text-xs text-muted-foreground">{addOwn.hint}</p>
              )}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
